import React from 'react';
import type { ChatChip } from '../../../work/ChipManager';
import type { ComposeAction } from '../composeActions';

/**
 * Build the Chips tab's actions array from the live chips state.
 * chips state is owned by ComposePanel root (NOT loaded inline) per
 * spec-triad — single source shared by ChipStrip + chipsTab + window.
 * storage subscription. Each chip becomes a kind:'fire' ComposeAction
 * (preserves existing chip-bar fire-immediately UX).
 *
 * Hidden chips are filtered out at this layer; the registry consumer
 * doesn't see them.
 */
export function buildChipsActions(chips: ChatChip[]): ComposeAction[] {
  return chips
    .filter((c) => !c.hidden)
    .map((chip): ComposeAction => ({
      kind: 'fire',
      id: chip.id,
      label: chip.label,
      prompt: chip.prompt,
      descriptor: chip.prompt.length > 80 ? `${chip.prompt.slice(0, 80)}…` : chip.prompt,
    }));
}

interface ChipsTabHeaderProps {
  onManageClick: () => void;
  darkMode: boolean;
}

/**
 * "+ Manage chips" button rendered in the tab-content header area when
 * Chips is the active tab. Opens the existing ChipManager modal via
 * onManageClick (parent ComposePanel owns the modal-open state).
 *
 * Dual-discoverability with the always-visible ⚙ icon on the chip-strip
 * (per UX original triad — settings-affordance, not the same anti-
 * pattern as compose-hierarchy two-doors).
 */
export const ChipsTabHeader: React.FC<ChipsTabHeaderProps> = ({ onManageClick, darkMode }) => {
  return (
    <div className={`flex items-center justify-end px-2 py-1 border-b ${
      darkMode ? 'border-gray-700/50' : 'border-gray-200'
    }`}>
      <button
        type="button"
        data-testid="compose-chips-manage"
        onClick={onManageClick}
        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded transition-colors ${
          darkMode
            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
            : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
        }`}
        aria-label="Manage chips"
      >
        + Manage chips
      </button>
    </div>
  );
};
